import { DatePicker, DatePickerSelectedValue } from '@storeblocks/datepicker';
import { nanoid } from 'nanoid';
import { ChangeEvent, type FC, useState } from 'react';
import styled from 'styled-components';

import { useTexts } from '@/hooks/useTexts';
import { useUserDateFnsLocale } from '@/i18n/hooks/useUserDateFnsLocale';
import { useUserLocale } from '@/i18n/hooks/useUserLocale';
import { datePickerDateFormat } from '@/util/datePickerDateFormat';

// Stable reference to dates.
const today = new Date();

interface Props {
  initialStartDate: Date;
  initialEndDate: Date;
  onChange(startDate: Date, endDate: Date): void;
}

export const DateRangeSelector: FC<Props> = ({
  initialStartDate,
  initialEndDate,
  onChange,
}) => {
  const texts = useTexts();

  // A page can have multiple of this component,
  // so we need to generate a unique ID for each one.
  const [componentIdStartDate] = useState(nanoid());
  const [componentIdEndDate] = useState(nanoid());

  const locale = useUserLocale();
  const dateFnsLocale = useUserDateFnsLocale();

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  const handleStartDateChange = (
    _: ChangeEvent<HTMLInputElement>,
    selectedDate: DatePickerSelectedValue,
  ): void => {
    if (selectedDate.isValid && selectedDate.date) {
      setStartDate(selectedDate.date);
      onChange(selectedDate.date, endDate);
    }
  };

  const handleEndDateChange = (
    _: ChangeEvent<HTMLInputElement>,
    selectedDate: DatePickerSelectedValue,
  ): void => {
    if (selectedDate.isValid && selectedDate.date) {
      setEndDate(selectedDate.date);
      onChange(startDate, selectedDate.date);
    }
  };

  return (
    <Container>
      <DatePicker
        id={componentIdStartDate}
        name="portfolio-search-by-start-date"
        locale={locale}
        value={startDate}
        onChange={handleStartDateChange}
        max={today}
        format={datePickerDateFormat(dateFnsLocale)}
        labelText={texts.dateFilter.fromDate}
        aria-label={texts.dateFilter.fromDate}
        selectMonthAriaLabel={texts.datepicker.selectMonthAriaLabel}
        selectYearAriaLabel={texts.datepicker.selectYearAriaLabel}
      />

      <DatePicker
        id={componentIdEndDate}
        name="portfolio-search-by-end-date"
        locale={locale}
        value={endDate}
        onChange={handleEndDateChange}
        max={today}
        format={datePickerDateFormat(dateFnsLocale)}
        labelText={texts.dateFilter.toDate}
        aria-label={texts.dateFilter.toDate}
        selectMonthAriaLabel={texts.datepicker.selectMonthAriaLabel}
        selectYearAriaLabel={texts.datepicker.selectYearAriaLabel}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  gap: 16px;

  align-items: end;
`;
